import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  formatTimeWithTimezone,
  formatCurrency,
} from "../../utils/displayHelpers";
import StaffSidebarLayout from "./StaffSidebarLayout";

const UnifiedScheduleCalendar = () => {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null);
  const [shopInfo, setShopInfo] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [businessBlockedDates, setBusinessBlockedDates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showLunchModal, setShowLunchModal] = useState(false);
  const [showDayBookingsModal, setShowDayBookingsModal] = useState(false);
  const [selectedDayBookings, setSelectedDayBookings] = useState([]);
  const [selectedDateStr, setSelectedDateStr] = useState("");
  const [showMultiLocationSection, setShowMultiLocationSection] =
    useState(false);
  const [multiLocationData, setMultiLocationData] = useState(null);
  const [loadingMultiLocation, setLoadingMultiLocation] = useState(false);
  const [editingException, setEditingException] = useState(null); // Track which exception is being edited
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictBookings, setConflictBookings] = useState([]);
  const [blockForm, setBlockForm] = useState({
    startDate: "",
    endDate: "",
    isFullDayBlock: true,
    timeBlocks: [{ startTime: "09:00", endTime: "17:00" }],
    type: "blocked",
    reason: "",
    recurring: {
      enabled: false,
      frequency: "weekly",
      daysOfWeek: [],
      occurrences: 1,
    },
  });
  const [lunchForm, setLunchForm] = useState({
    startDate: "",
    startTime: "12:00",
    endTime: "12:30",
    daysOfWeek: [1, 2, 3, 4, 5], // Default: Monday-Friday
  });

  // Helper to format dates for display (handles both Date objects and YYYY-MM-DD strings)
  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return "";

    // If it's already a string in YYYY-MM-DD format, convert to MM/DD/YYYY
    if (
      typeof dateValue === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ) {
      const [year, month, day] = dateValue.split("-");
      return `${month}/${day}/${year}`;
    }

    // If it's a Date object or date string, parse and format
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return dateValue; // Return as-is if invalid

    // Add time to avoid timezone offset
    const adjusted = new Date(
      date.getTime() + Math.abs(date.getTimezoneOffset() * 60000),
    );
    return `${String(adjusted.getMonth() + 1).padStart(2, "0")}/${String(adjusted.getDate()).padStart(2, "0")}/${adjusted.getFullYear()}`;
  };

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  // Fetch schedule and shop info
  const fetchSchedule = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      // Fetch shop info which includes schedule
      const shopRes = await fetch(`${API_BASE_URL}/api/staff/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (shopRes.ok) {
        const data = await shopRes.json();
        // Set both schedule and shop info from the same response
        if (data.schedule) {
          setSchedule(data.schedule);
        } else {
          // Set default schedule if none exists
          setSchedule({
            weeklyHours: {
              monday: { isAvailable: true, start: "09:00", end: "17:00" },
              tuesday: { isAvailable: true, start: "09:00", end: "17:00" },
              wednesday: { isAvailable: true, start: "09:00", end: "17:00" },
              thursday: { isAvailable: true, start: "09:00", end: "17:00" },
              friday: { isAvailable: true, start: "09:00", end: "17:00" },
              saturday: { isAvailable: false, start: "09:00", end: "17:00" },
              sunday: { isAvailable: false, start: "09:00", end: "17:00" },
            },
          });
        }
        if (data.shop) {
          setShopInfo(data.shop);
        }
      } else {
        const errorData = await shopRes.json().catch(() => ({}));
        // Set default schedule if API fails
        setSchedule({
          weeklyHours: {
            monday: { isAvailable: true, start: "09:00", end: "17:00" },
            tuesday: { isAvailable: true, start: "09:00", end: "17:00" },
            wednesday: { isAvailable: true, start: "09:00", end: "17:00" },
            thursday: { isAvailable: true, start: "09:00", end: "17:00" },
            friday: { isAvailable: true, start: "09:00", end: "17:00" },
            saturday: { isAvailable: false, start: "09:00", end: "17:00" },
            sunday: { isAvailable: false, start: "09:00", end: "17:00" },
          },
        });
      }
    } catch (error) {
      // Set default schedule on error
      setSchedule({
        weeklyHours: {
          monday: { isAvailable: true, start: "09:00", end: "17:00" },
          tuesday: { isAvailable: true, start: "09:00", end: "17:00" },
          wednesday: { isAvailable: true, start: "09:00", end: "17:00" },
          thursday: { isAvailable: true, start: "09:00", end: "17:00" },
          friday: { isAvailable: true, start: "09:00", end: "17:00" },
          saturday: { isAvailable: false, start: "09:00", end: "17:00" },
          sunday: { isAvailable: false, start: "09:00", end: "17:00" },
        },
      });
    }
  }, [API_BASE_URL, navigate]);

  // Fetch exceptions
  const fetchExceptions = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/staff`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setExceptions(data.exceptions || []);
      }
    } catch (error) {
    }
  }, [API_BASE_URL]);

  // Fetch business-level blocked dates
  const fetchBusinessBlockedDates = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");

      // Get staff profile to extract businessId
      const staffRes = await fetch(`${API_BASE_URL}/api/staff/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        const staff = staffData.staff || staffData;
        const businessId =
          typeof staff.businessId === "object"
            ? staff.businessId?._id
            : staff.businessId;

        if (businessId) {
          const blockedRes = await fetch(
            `${API_BASE_URL}/api/business/${businessId}/blocked-dates`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (blockedRes.ok) {
            const data = await blockedRes.json();
            setBusinessBlockedDates(data.blockedDates || []);
          }
        }
      }
    } catch (error) {
    }
  }, [API_BASE_URL]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(`${API_BASE_URL}/api/bookings/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchSchedule();
    fetchExceptions();
    fetchBusinessBlockedDates();
    fetchBookings();
  }, [
    fetchSchedule,
    fetchExceptions,
    fetchBusinessBlockedDates,
    fetchBookings,
  ]);

  // Fetch multi-location status
  const fetchMultiLocationStatus = useCallback(async () => {
    if (!showMultiLocationSection) return;

    setLoadingMultiLocation(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/multi-location/my-request`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setMultiLocationData(data);
      }
    } catch (error) {
    } finally {
      setLoadingMultiLocation(false);
    }
  }, [API_BASE_URL, showMultiLocationSection]);

  useEffect(() => {
    fetchMultiLocationStatus();
  }, [fetchMultiLocationStatus]);

  // Handle schedule update
  const handleScheduleUpdate = async (updatedWeeklyHours) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(`${API_BASE_URL}/api/staff/schedule`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          weeklyHours: updatedWeeklyHours,
          bufferMinutes: schedule.bufferMinutes || 0,
        }),
      });

      if (response.ok) {
        alert("Schedule updated successfully!");
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.message || "Failed to update schedule");
      }
    } catch (error) {
      alert("Failed to update schedule");
    }
  };

  // Handle block creation
  const handleBlockSubmit = async () => {
    // Validate end date is not before start date
    if (blockForm.startDate && blockForm.endDate) {
      const start = new Date(blockForm.startDate);
      const end = new Date(blockForm.endDate);
      if (end < start) {
        alert("❌ End date cannot be before start date");
        return;
      }
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const url = editingException
        ? `${API_BASE_URL}/api/schedule-exceptions/${editingException._id}`
        : `${API_BASE_URL}/api/schedule-exceptions`;
      const method = editingException ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(blockForm),
      });

      if (response.ok) {
        const data = await response.json();
        const action = editingException ? "updated" : "created";

        // Check if requires approval
        if (data.requiresApproval) {
          alert(
            "⏳ Time-off request submitted for approval. You will be notified when the owner reviews your request.",
          );
        } else {
          alert(`✅ Block ${action} immediately (no approval needed)`);
        }

        setShowBlockModal(false);
        setEditingException(null);
        fetchExceptions();
        fetchBookings();
        // Reset form
        setBlockForm({
          startDate: "",
          endDate: "",
          isFullDayBlock: true,
          timeBlocks: [{ startTime: "09:00", endTime: "17:00" }],
          type: "blocked",
          reason: "",
          recurring: {
            enabled: false,
            frequency: "weekly",
            daysOfWeek: [],
            occurrences: 1,
          },
        });
      } else {
        const error = await response.json();

        // CONFLICT DETECTION: Show conflict modal with booking details
        if (response.status === 409 && error.error === "booking_conflict") {
          setConflictBookings(error.affectedBookings || []);
          setShowConflictModal(true);
          setShowBlockModal(false); // Close block modal
          return;
        }

        alert(
          error.message ||
            `Failed to ${editingException ? "update" : "create"} block`,
        );
      }
    } catch (error) {
      alert(`Failed to ${editingException ? "update" : "create"} block`);
    }
  };

  // Handle editing a blocked date
  const handleEditBlock = (exception) => {
    // Format dates for input fields (YYYY-MM-DD)
    const formatDate = (dateStr) => {
      // If already in YYYY-MM-DD format, return as-is
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // Otherwise parse and format using UTC to avoid timezone shift
      const date = new Date(dateStr);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setEditingException(exception);
    setBlockForm({
      startDate: formatDate(exception.startDate),
      endDate: formatDate(exception.endDate),
      isFullDayBlock: exception.isFullDayBlock,
      timeBlocks:
        exception.timeBlocks && exception.timeBlocks.length > 0
          ? exception.timeBlocks
          : [{ startTime: "09:00", endTime: "17:00" }],
      type: exception.type || "blocked",
      reason: exception.reason || "",
      recurring: exception.recurring || {
        enabled: false,
        frequency: "weekly",
        daysOfWeek: [],
        occurrences: 1,
      },
    });
    setShowBlockModal(true);
  };

  // Handle block removal
  const handleRemoveBlock = async (exceptionId) => {
    if (!window.confirm("Are you sure you want to remove this block?")) return;

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/${exceptionId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        alert("Block removed successfully!");
        fetchExceptions();
      } else {
        const error = await response.json();
        alert(error.message || "Failed to remove block");
      }
    } catch (error) {
      alert("Failed to remove block");
    }
  };

  // Date navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date
  const formatMonthYear = () => {
    return currentDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  // Check if date is blocked (FULL-DAY blocks only - not time-specific lunch blocks)
  const isDateBlocked = (date) => {
    // Check staff-level exceptions (only full-day blocks)
    const staffBlocked = exceptions.some((ex) => {
      const exDate = new Date(date);
      exDate.setHours(0, 0, 0, 0);
      const exStart = new Date(ex.startDate);
      exStart.setHours(0, 0, 0, 0);
      const exEnd = new Date(ex.endDate);
      exEnd.setHours(0, 0, 0, 0);

      // Only treat as "blocked" if it's a full-day block AND active
      return (
        ex.status === "active" &&
        ex.isFullDayBlock &&
        exDate >= exStart &&
        exDate <= exEnd
      );
    });

    // Check business-level blocked dates
    const businessBlocked = businessBlockedDates.some((blocked) => {
      // Convert date to YYYY-MM-DD string for comparison
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateString = `${year}-${month}-${day}`;

      // Handle both string dates (YYYY-MM-DD) and Date objects from database
      let startDate, endDate;

      if (
        typeof blocked.startDate === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(blocked.startDate)
      ) {
        startDate = blocked.startDate;
        endDate = blocked.endDate;
      } else {
        // Convert Date object to YYYY-MM-DD string (use UTC to avoid timezone offset)
        const start = new Date(blocked.startDate);
        const end = new Date(blocked.endDate);
        startDate = `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, "0")}-${String(start.getUTCDate()).padStart(2, "0")}`;
        endDate = `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, "0")}-${String(end.getUTCDate()).padStart(2, "0")}`;
      }

      // String comparison works correctly for YYYY-MM-DD format
      return dateString >= startDate && dateString <= endDate;
    });

    return staffBlocked || businessBlocked;
  };

  // Get bookings for date
  const getBookingsForDate = (date) => {
    // Format date as YYYY-MM-DD for comparison (avoid timezone conversion)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    return bookings.filter((booking) => {
      // booking.appointmentDate is already in YYYY-MM-DD format
      return booking.appointmentDate === dateStr;
    });
  };

  // Get time-specific blocks for a date (e.g., lunch breaks)
  const getTimeBlocksForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    return exceptions.filter((ex) => {
      if (ex.isFullDayBlock || ex.status !== "active") return false;

      // For recurring exceptions, check if date matches the pattern
      if (ex.recurring && ex.recurring.enabled) {
        const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.

        // Check if this day of week is included
        if (ex.recurring.daysOfWeek && ex.recurring.daysOfWeek.length > 0) {
          if (!ex.recurring.daysOfWeek.includes(dayOfWeek)) {
            return false;
          }
        }

        // Check if date is after start date (use UTC to avoid timezone shifts)
        const exStart = new Date(ex.startDate);
        const exStartDateStr = `${exStart.getUTCFullYear()}-${String(exStart.getUTCMonth() + 1).padStart(2, "0")}-${String(exStart.getUTCDate()).padStart(2, "0")}`;

        if (dateStr < exStartDateStr) return false;

        // If has endsOn date, check if before that
        if (ex.recurring.endsOn) {
          const endsOn = new Date(ex.recurring.endsOn);
          const endsOnDateStr = `${endsOn.getUTCFullYear()}-${String(endsOn.getUTCMonth() + 1).padStart(2, "0")}-${String(endsOn.getUTCDate()).padStart(2, "0")}`;
          if (dateStr > endsOnDateStr) return false;
        }

        return true;
      }

      // For non-recurring, check if date is within range (use UTC to avoid timezone shifts)
      const exStart = new Date(ex.startDate);
      const exStartDateStr = `${exStart.getUTCFullYear()}-${String(exStart.getUTCMonth() + 1).padStart(2, "0")}-${String(exStart.getUTCDate()).padStart(2, "0")}`;

      const exEnd = new Date(ex.endDate);
      const exEndDateStr = `${exEnd.getUTCFullYear()}-${String(exEnd.getUTCMonth() + 1).padStart(2, "0")}-${String(exEnd.getUTCDate()).padStart(2, "0")}`;

      return dateStr >= exStartDateStr && dateStr <= exEndDateStr;
    });
  };

  // Quick block date
  const quickBlockDate = (date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    setBlockForm((prev) => ({
      ...prev,
      startDate: dateStr,
      endDate: dateStr,
    }));
    setShowBlockModal(true);
  };

  // Helper: Format time to 12-hour AM/PM
  const formatTime12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Render weekly schedule
  const renderWeeklySchedule = () => {
    if (!schedule || !schedule.weeklyHours) {
      return (
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <p>Loading schedule...</p>
        </div>
      );
    }

    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const isMobile = window.innerWidth < 768;

    return (
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <h2
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Weekly Schedule
          </h2>
        </div>

        {/* Buffer Time Setting - Moved to top */}
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              fontWeight: "600",
              color: "#92400e",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ fontSize: "16px" }}>⏱️</span>
            Buffer Time Between Appointments
          </h3>
          <p
            style={{ fontSize: "12px", color: "#78350f", margin: "0 0 12px 0" }}
          >
            Add time between appointments for cleanup, preparation, or rest
            (0-60 minutes).
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="number"
                min="0"
                max="60"
                value={
                  schedule.bufferMinutes !== undefined
                    ? schedule.bufferMinutes
                    : 0
                }
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? 0 : parseInt(e.target.value);
                  const clamped = Math.min(60, Math.max(0, value));
                  setSchedule((prev) => ({ ...prev, bufferMinutes: clamped }));
                }}
                style={{
                  width: "80px",
                  padding: "10px",
                  border: "2px solid #fbbf24",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              />
              <span
                style={{
                  fontSize: "14px",
                  color: "#92400e",
                  fontWeight: "500",
                }}
              >
                minutes
              </span>
            </div>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("ogestyle_token");
                  const response = await fetch(
                    `${API_BASE_URL}/api/staff/schedule`,
                    {
                      method: "PUT",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        weeklyHours: schedule.weeklyHours,
                        bufferMinutes:
                          schedule.bufferMinutes !== undefined
                            ? schedule.bufferMinutes
                            : 0,
                      }),
                    },
                  );
                  if (response.ok) {
                    alert("Buffer time saved successfully!");
                  } else {
                    alert("Failed to save buffer time");
                  }
                } catch (error) {
                  alert("Error saving buffer time");
                }
              }}
              style={{
                padding: "8px 16px",
                background: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Save Buffer Time
            </button>
          </div>
          <p
            style={{
              fontSize: "11px",
              color: "#92400e",
              margin: "8px 0 0 0",
              fontStyle: "italic",
            }}
          >
            💡 Tip: Set to 0 for no buffer. Higher buffer time = fewer bookings
            per day, but more preparation time
          </p>
        </div>

        {/* Apply Business Hours - Moved below buffer time */}
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              fontWeight: "600",
              color: "#1e40af",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ fontSize: "16px" }}>⚡</span>
            Apply Business Hours to My Schedule
          </h3>
          <p
            style={{ fontSize: "12px", color: "#64748b", margin: "0 0 16px 0" }}
          >
            Set your schedule to match the business operating hours shown above,
            then customize individual days as needed below.
          </p>
          <button
            onClick={() => {
              if (!shopInfo || !shopInfo.hours) {
                alert("Business hours not found");
                return;
              }
              // Map lowercase day names to capitalized (monday -> Monday)
              const dayMapping = {
                monday: "Monday",
                tuesday: "Tuesday",
                wednesday: "Wednesday",
                thursday: "Thursday",
                friday: "Friday",
                saturday: "Saturday",
                sunday: "Sunday",
              };

              const updated = { ...schedule };
              days.forEach((day) => {
                // Shop uses capitalized days (Monday, Tuesday, etc.)
                const shopDayKey = dayMapping[day];
                const shopDay = shopInfo.hours[shopDayKey];
                if (shopDay && shopDay.active) {
                  updated.weeklyHours[day] = {
                    isAvailable: true,
                    start: shopDay.open || "09:00",
                    end: shopDay.close || "18:00",
                  };
                } else {
                  updated.weeklyHours[day] = {
                    isAvailable: false,
                    start: "09:00",
                    end: "18:00",
                  };
                }
              });
              setSchedule(updated);
            }}
            style={{
              padding: isMobile ? "12px 24px" : "10px 20px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              whiteSpace: "nowrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            Apply Business Hours
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? "8px" : "12px",
          }}
        >
          {days.map((day) => {
            const daySchedule = schedule.weeklyHours[day] || {
              isAvailable: false,
              start: "09:00",
              end: "17:00",
            };
            return (
              <div
                key={day}
                style={{
                  padding: isMobile ? "10px" : "12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  background: daySchedule.isAvailable ? "white" : "#f9fafb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "13px" : "14px",
                      textTransform: "capitalize",
                    }}
                  >
                    {day}
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={daySchedule.isAvailable}
                      onChange={(e) => {
                        const updated = { ...schedule };
                        updated.weeklyHours[day].isAvailable = e.target.checked;
                        setSchedule(updated);
                      }}
                      style={{ width: "14px", height: "14px" }}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#6b7280",
                      }}
                    >
                      Available
                    </span>
                  </label>
                </div>
                {daySchedule.isAvailable && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="time"
                      value={daySchedule.start}
                      onChange={(e) => {
                        const updated = { ...schedule };
                        updated.weeklyHours[day].start = e.target.value;
                        setSchedule(updated);
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? "4px" : "6px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: isMobile ? "11px" : "12px",
                      }}
                    />
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: isMobile ? "10px" : "12px",
                      }}
                    >
                      to
                    </span>
                    <input
                      type="time"
                      value={daySchedule.end}
                      onChange={(e) => {
                        const updated = { ...schedule };
                        updated.weeklyHours[day].end = e.target.value;
                        setSchedule(updated);
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? "4px" : "6px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: isMobile ? "11px" : "12px",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Save Button at Bottom */}
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => handleScheduleUpdate(schedule.weeklyHours)}
            style={{
              padding: isMobile ? "8px 20px" : "10px 24px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: isMobile ? "14px" : "15px",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
            }}
          >
            Save Schedule
          </button>
        </div>
      </div>
    );
  };

  // Render blocked dates list
  const renderBlockedDates = () => {
    const isMobile = window.innerWidth < 768;

    // Include both active and pending_approval status
    const hasStaffBlocks =
      exceptions.filter(
        (ex) => ex.status === "active" || ex.status === "pending_approval",
      ).length > 0;
    const hasBusinessBlocks = businessBlockedDates.length > 0;

    if (!hasStaffBlocks && !hasBusinessBlocks) return null;

    return (
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "16px" : "18px",
            fontWeight: "600",
            marginBottom: "12px",
          }}
        >
          🚫 Off Days and Lunch
        </h2>

        {/* Business-Level Blocked Dates */}
        {hasBusinessBlocks && (
          <div style={{ marginBottom: hasStaffBlocks ? "16px" : "0" }}>
            <h3
              style={{
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              🏢 Business-Wide (All Locations)
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {businessBlockedDates.map((blocked) => (
                <div
                  key={blocked._id}
                  style={{
                    padding: isMobile ? "10px" : "12px",
                    background: "#fef3c7",
                    borderRadius: "8px",
                    borderLeft: "4px solid #f59e0b",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: isMobile ? "13px" : "14px",
                      marginBottom: "4px",
                    }}
                  >
                    {blocked.reason}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "11px" : "12px",
                      color: "#6b7280",
                    }}
                  >
                    {formatDisplayDate(blocked.startDate)} -{" "}
                    {formatDisplayDate(blocked.endDate)}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "10px" : "11px",
                      color: "#92400e",
                      marginTop: "4px",
                      fontStyle: "italic",
                    }}
                  >
                    Set by business owner - Cannot be modified
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Staff-Level Blocked Dates */}
        {hasStaffBlocks && (
          <div>
            <h3
              style={{
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              👤 Your Personal Blocks
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {exceptions
                .filter(
                  (ex) =>
                    ex.status === "active" || ex.status === "pending_approval",
                )
                .map((ex) => (
                  <div
                    key={ex._id}
                    style={{
                      padding: isMobile ? "10px" : "12px",
                      background:
                        ex.status === "pending_approval"
                          ? "#fef9c3"
                          : "#fee2e2",
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      borderLeft:
                        ex.status === "pending_approval"
                          ? "4px solid #eab308"
                          : "none",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: isMobile ? "13px" : "14px",
                          fontWeight: "600",
                        }}
                      >
                        {ex.status === "pending_approval" && "⏳ "}
                        {new Date(ex.startDate).toLocaleDateString()} -{" "}
                        {new Date(ex.endDate).toLocaleDateString()}
                        {ex.recurring?.enabled && " 🔁"}
                      </div>
                      {ex.reason && (
                        <div
                          style={{
                            fontSize: isMobile ? "11px" : "12px",
                            color: "#6b7280",
                            marginTop: "4px",
                          }}
                        >
                          {ex.reason}
                        </div>
                      )}
                      {ex.status === "pending_approval" && (
                        <div
                          style={{
                            fontSize: isMobile ? "10px" : "11px",
                            color: "#92400e",
                            marginTop: "4px",
                            fontStyle: "italic",
                          }}
                        >
                          Pending owner approval
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {ex.status === "active" && (
                        <>
                          <button
                            onClick={() => handleEditBlock(ex)}
                            style={{
                              padding: isMobile ? "4px 8px" : "6px 12px",
                              background: "#2563eb",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: isMobile ? "11px" : "12px",
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleRemoveBlock(ex._id)}
                            style={{
                              padding: isMobile ? "4px 8px" : "6px 12px",
                              background: "#dc2626",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: isMobile ? "11px" : "12px",
                            }}
                          >
                            Remove
                          </button>
                        </>
                      )}
                      {ex.status === "pending_approval" && (
                        <button
                          onClick={() => handleRemoveBlock(ex._id)}
                          style={{
                            padding: isMobile ? "4px 8px" : "6px 12px",
                            background: "#dc2626",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: isMobile ? "11px" : "12px",
                          }}
                        >
                          Cancel Request
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render month calendar view - using Calendar.js clean UI
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const weeks = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Group into weeks
    while (days.length) {
      weeks.push(days.splice(0, 7));
    }

    const isMobile = window.innerWidth < 768;

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Weekday headers */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "12px",
          }}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              style={{
                textAlign: "center",
                fontWeight: "600",
                fontSize: isMobile ? "11px" : "14px",
                color: "#6b7280",
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {weeks.map((week, weekIdx) => (
            <div
              key={weekIdx}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                flex: 1,
                borderBottom:
                  weekIdx < weeks.length - 1 ? "1px solid #e5e7eb" : "none",
                minHeight: isMobile ? "60px" : "100px",
              }}
            >
              {week.map((date, dayIdx) => {
                if (!date) {
                  return (
                    <div
                      key={dayIdx}
                      style={{
                        borderRight: dayIdx < 6 ? "1px solid #e5e7eb" : "none",
                        padding: isMobile ? "4px" : "8px",
                      }}
                    />
                  );
                }

                const dayBookings = getBookingsForDate(date);
                const isToday =
                  date.toDateString() === new Date().toDateString();
                const isBlocked = isDateBlocked(date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const compareDate = new Date(date);
                compareDate.setHours(0, 0, 0, 0);
                const isPast = compareDate < today;

                return (
                  <div
                    key={dayIdx}
                    style={{
                      borderRight: dayIdx < 6 ? "1px solid #e5e7eb" : "none",
                      padding: isMobile ? "4px" : "8px",
                      cursor: isPast ? "default" : "pointer",
                      background: isBlocked
                        ? "#fee2e2"
                        : isToday
                          ? "#eff6ff"
                          : isPast
                            ? "#f3f4f6"
                            : "white",
                      transition: "background 0.2s",
                      position: "relative",
                      opacity: isPast ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!isBlocked && !isToday && !isPast)
                        e.currentTarget.style.background = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      if (!isBlocked && !isToday && !isPast)
                        e.currentTarget.style.background = "white";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        marginBottom: "4px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "14px",
                          fontWeight: isToday ? "700" : "500",
                          color: isToday
                            ? "#3b82f6"
                            : isPast
                              ? "#9ca3af"
                              : "#1f2937",
                        }}
                      >
                        {date.getDate()}
                      </div>
                      {!isBlocked && !isPast && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            quickBlockDate(date);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: isMobile ? "10px" : "12px",
                            opacity: 0.6,
                            padding: "2px",
                          }}
                          title="Block this date"
                        >
                          🚫
                        </button>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      {/* Display bookings */}
                      {dayBookings.slice(0, isMobile ? 2 : 3).map((booking) => (
                        <div
                          key={booking._id}
                          onClick={() =>
                            navigate(
                              `/dashboard/professional/bookings/${booking._id}`,
                            )
                          }
                          style={{
                            fontSize: isMobile ? "9px" : "11px",
                            padding: "2px 4px",
                            borderRadius: "3px",
                            background:
                              booking.status === "confirmed"
                                ? "#dbeafe"
                                : booking.status === "completed"
                                  ? "#d1fae5"
                                  : "#fee2e2",
                            color:
                              booking.status === "confirmed"
                                ? "#1e40af"
                                : booking.status === "completed"
                                  ? "#065f46"
                                  : "#991b1b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                          }}
                        >
                          {formatTimeWithTimezone(
                            booking.startTime,
                            booking.appointmentDate,
                            shopInfo?.timezone || "America/Chicago",
                          )}{" "}
                          {booking.serviceName}
                        </div>
                      ))}
                      {/* Display time-specific blocks (e.g., lunch) */}
                      {getTimeBlocksForDate(date).map((block) => (
                        <div
                          key={block._id}
                          style={{
                            fontSize: isMobile ? "9px" : "11px",
                            padding: "2px 4px",
                            borderRadius: "3px",
                            background: "#fef3c7",
                            color: "#92400e",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {block.timeBlocks && block.timeBlocks[0]
                            ? formatTime12Hour(block.timeBlocks[0].startTime)
                            : ""}{" "}
                          {block.reason || "Blocked"}
                        </div>
                      ))}
                      {dayBookings.length > (isMobile ? 2 : 3) && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDayBookings(dayBookings);
                            setSelectedDateStr(
                              new Date(date).toLocaleDateString("en-US", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              }),
                            );
                            setShowDayBookingsModal(true);
                          }}
                          style={{
                            fontSize: isMobile ? "9px" : "11px",
                            color: "#2d6da3",
                            textAlign: "center",
                            cursor: "pointer",
                            fontWeight: "600",
                            padding: "2px 4px",
                            borderRadius: "3px",
                            background: "#f3e8ff",
                          }}
                        >
                          +{dayBookings.length - (isMobile ? 2 : 3)} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const isMobile = window.innerWidth < 768;

  // Render multi-location section
  const renderMultiLocationSection = () => {
    return (
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: isMobile ? "16px" : "24px",
        }}
      >
        <div
          onClick={() => setShowMultiLocationSection(!showMultiLocationSection)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            padding: isMobile ? "8px" : "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: isMobile ? "16px" : "18px" }}>📍</span>
            <h3
              style={{
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: "600",
                margin: 0,
              }}
            >
              Multi-Location Access
            </h3>
          </div>
          <span
            style={{
              fontSize: isMobile ? "18px" : "20px",
              transition: "transform 0.2s",
              transform: showMultiLocationSection
                ? "rotate(180deg)"
                : "rotate(0deg)",
            }}
          >
            ▼
          </span>
        </div>

        {showMultiLocationSection && (
          <div
            style={{
              marginTop: "16px",
              paddingTop: "16px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            {loadingMultiLocation ? (
              <p style={{ textAlign: "center", color: "#6b7280" }}>
                Loading...
              </p>
            ) : multiLocationData ? (
              <>
                {/* Status Badge */}
                <div style={{ marginBottom: "16px" }}>
                  {multiLocationData.requestStatus === "approved" && (
                    <div>
                      <div
                        style={{
                          display: "inline-block",
                          padding: "6px 12px",
                          background: "#d1fae5",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#065f46",
                        }}
                      >
                        ✓ Multi-Location Approved
                      </div>
                      {multiLocationData.approvedLocations &&
                        multiLocationData.approvedLocations.length > 0 && (
                          <div style={{ marginTop: "12px" }}>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                marginBottom: "6px",
                                color: "#374151",
                              }}
                            >
                              You can work at{" "}
                              {multiLocationData.approvedLocations.length}{" "}
                              location(s):
                            </p>
                            <p style={{ fontSize: "12px", color: "#6b7280" }}>
                              Use the location selector when creating schedules
                              to choose which location you're working at.
                            </p>
                          </div>
                        )}
                    </div>
                  )}

                  {multiLocationData.requestStatus === "pending" && (
                    <div>
                      <div
                        style={{
                          display: "inline-block",
                          padding: "6px 12px",
                          background: "#fef3c7",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#92400e",
                        }}
                      >
                        ⏳ Request Pending
                      </div>
                      <p
                        style={{
                          marginTop: "8px",
                          fontSize: "12px",
                          color: "#6b7280",
                        }}
                      >
                        Your multi-location request is being reviewed by your
                        business owner.
                      </p>
                    </div>
                  )}

                  {(!multiLocationData.requestStatus ||
                    multiLocationData.requestStatus === "not_requested") && (
                    <div>
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#6b7280",
                          marginBottom: "12px",
                        }}
                      >
                        Request permission to work at multiple business
                        locations. Your schedules and bookings will be managed
                        across all approved locations.
                      </p>
                      <button
                        onClick={() =>
                          navigate("/dashboard/professional/multi-location")
                        }
                        style={{
                          padding: "8px 16px",
                          background: "#00509d",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Request Multi-Location Access
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p style={{ fontSize: "12px", color: "#6b7280" }}>
                Unable to load multi-location status
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <StaffSidebarLayout>
      <div
        style={{
          padding: isMobile ? "16px" : "24px",
          maxWidth: "1400px",
          margin: "0 auto",
          paddingBottom: isMobile ? "100px" : "60px",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: isMobile ? "16px" : "24px" }}>
          <h1
            style={{
              fontSize: isMobile ? "20px" : "28px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            Schedule & Calendar
          </h1>
          <p style={{ color: "#6b7280", fontSize: isMobile ? "13px" : "14px" }}>
            Manage your availability and view your calendar
          </p>
        </div>

        {/* Multi-Location Access Section */}
        {/* HIDDEN: Multi-location feature not ready for production - Dec 2025 */}
        {/* {renderMultiLocationSection()} */}

        {/* Shop Hours Reference */}
        {shopInfo && (
          <div
            style={{
              background: "#e0f2fe",
              border: "1px solid #7dd3fc",
              borderRadius: "12px",
              padding: isMobile ? "12px" : "16px",
              marginBottom: isMobile ? "16px" : "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: isMobile ? "16px" : "18px" }}>📍</span>
              <strong
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  color: "#0369a1",
                }}
              >
                {shopInfo.name}
              </strong>
            </div>
            {shopInfo.hours && Object.keys(shopInfo.hours).length > 0 ? (
              <>
                <div
                  style={{
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#075985",
                    marginBottom: "8px",
                  }}
                >
                  Your hours must be within shop hours. Shop is open:
                </div>
                <ul
                  style={{
                    margin: "0",
                    paddingLeft: "20px",
                    fontSize: isMobile ? "11px" : "12px",
                    color: "#0c4a6e",
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                    gap: isMobile ? "4px" : "6px",
                  }}
                >
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => {
                    const shopDay = shopInfo.hours?.[day];
                    if (shopDay?.active) {
                      return (
                        <li key={day}>
                          <strong>{day}:</strong>{" "}
                          {formatTime12Hour(shopDay.open)} -{" "}
                          {formatTime12Hour(shopDay.close)}
                        </li>
                      );
                    }
                    return (
                      <li key={day}>
                        <strong>{day}:</strong> Closed
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <div
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  color: "#dc2626",
                  padding: "8px",
                  background: "#fee2e2",
                  borderRadius: "6px",
                }}
              >
                ⚠️ Shop business hours not configured yet. Please contact the
                shop owner to set up business hours before creating your
                schedule.
              </div>
            )}
          </div>
        )}

        {/* Weekly Schedule Section */}
        {renderWeeklySchedule()}

        {/* Blocked Dates Section */}
        {renderBlockedDates()}

        {/* Calendar Section */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: isMobile ? "12px" : "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          {/* Calendar Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                margin: 0,
              }}
            >
              Calendar
            </h2>

            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={goToPrevious}
                style={{
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "14px",
                }}
              >
                ← Prev
              </button>

              <button
                onClick={goToToday}
                style={{
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "500",
                }}
              >
                Today
              </button>

              <button
                onClick={goToNext}
                style={{
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "14px",
                }}
              >
                Next →
              </button>

              <button
                onClick={() => setShowBlockModal(true)}
                style={{
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "500",
                }}
              >
                Time Off Days
              </button>

              <button
                onClick={() => {
                  // Get today as start date
                  const today = new Date();
                  const year = today.getFullYear();
                  const month = String(today.getMonth() + 1).padStart(2, "0");
                  const day = String(today.getDate()).padStart(2, "0");
                  const todayStr = `${year}-${month}-${day}`;

                  setLunchForm({
                    startDate: todayStr,
                    startTime: "12:00",
                    endTime: "12:30",
                  });
                  setShowLunchModal(true);
                }}
                style={{
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  background: "#f59e0b",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
                title="Block recurring lunch break (12pm-12:30pm, Mon-Fri)"
              >
                🍽️ Block Lunch
              </button>
            </div>
          </div>

          {/* Month/Year Display */}
          <div
            style={{
              fontSize: isMobile ? "16px" : "20px",
              fontWeight: "600",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            {formatMonthYear()}
          </div>

          {/* Calendar View */}
          {renderMonthView()}
        </div>
      </div>

      {/* Block Modal */}
      {showBlockModal && (
        <>
          <div
            onClick={() => setShowBlockModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 999,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "12px",
              padding: isMobile ? "20px" : "24px",
              zIndex: 1000,
              maxWidth: isMobile ? "90%" : "500px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              {editingException ? "Edit Time Off" : "Request Time Off Days"}
            </h2>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Start Date
              </label>
              <input
                type="date"
                value={blockForm.startDate}
                onChange={(e) =>
                  setBlockForm((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                End Date
              </label>
              <input
                type="date"
                value={blockForm.endDate}
                onChange={(e) =>
                  setBlockForm((prev) => ({ ...prev, endDate: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={blockForm.isFullDayBlock}
                  onChange={(e) =>
                    setBlockForm((prev) => ({
                      ...prev,
                      isFullDayBlock: e.target.checked,
                    }))
                  }
                />
                <span style={{ fontSize: "14px" }}>Block entire day(s)</span>
              </label>
            </div>

            {!blockForm.isFullDayBlock && (
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Time Block
                </label>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <input
                    type="time"
                    value={blockForm.timeBlocks[0].startTime}
                    onChange={(e) =>
                      setBlockForm((prev) => ({
                        ...prev,
                        timeBlocks: [
                          { ...prev.timeBlocks[0], startTime: e.target.value },
                        ],
                      }))
                    }
                    style={{
                      flex: 1,
                      padding: "8px",
                      border: "2px solid #2d6da3",
                      borderRadius: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ fontWeight: "500", color: "#6b7280" }}>
                    to
                  </span>
                  <input
                    type="time"
                    value={blockForm.timeBlocks[0].endTime}
                    onChange={(e) =>
                      setBlockForm((prev) => ({
                        ...prev,
                        timeBlocks: [
                          { ...prev.timeBlocks[0], endTime: e.target.value },
                        ],
                      }))
                    }
                    style={{
                      flex: 1,
                      padding: "8px",
                      border: "2px solid #2d6da3",
                      borderRadius: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#10b981",
                    marginTop: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  ✓ Click time to select, time picker will close automatically
                </p>
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  marginBottom: "8px",
                }}
              >
                <input
                  type="checkbox"
                  checked={blockForm.recurring.enabled}
                  onChange={(e) =>
                    setBlockForm((prev) => ({
                      ...prev,
                      recurring: {
                        ...prev.recurring,
                        enabled: e.target.checked,
                      },
                    }))
                  }
                />
                <span style={{ fontSize: "14px" }}>Recurring</span>
              </label>

              {blockForm.recurring.enabled && (
                <>
                  <select
                    value={blockForm.recurring.frequency}
                    onChange={(e) =>
                      setBlockForm((prev) => ({
                        ...prev,
                        recurring: {
                          ...prev.recurring,
                          frequency: e.target.value,
                        },
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                  </select>

                  <div style={{ marginBottom: "8px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "13px",
                      }}
                    >
                      Days of Week
                    </label>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                        (day, idx) => (
                          <label
                            key={day}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={blockForm.recurring.daysOfWeek.includes(
                                idx,
                              )}
                              onChange={(e) => {
                                const days = e.target.checked
                                  ? [...blockForm.recurring.daysOfWeek, idx]
                                  : blockForm.recurring.daysOfWeek.filter(
                                      (d) => d !== idx,
                                    );
                                setBlockForm((prev) => ({
                                  ...prev,
                                  recurring: {
                                    ...prev.recurring,
                                    daysOfWeek: days,
                                  },
                                }));
                              }}
                            />
                            {day}
                          </label>
                        ),
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "13px",
                      }}
                    >
                      How many times to repeat
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={blockForm.recurring.occurrences}
                      onChange={(e) =>
                        setBlockForm((prev) => ({
                          ...prev,
                          recurring: {
                            ...prev.recurring,
                            occurrences: parseInt(e.target.value) || 1,
                          },
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#6b7280",
                        marginTop: "4px",
                        marginBottom: 0,
                      }}
                    >
                      Example: 52 = block every{" "}
                      {blockForm.recurring.frequency === "weekly"
                        ? "week"
                        : blockForm.recurring.frequency === "biweekly"
                          ? "2 weeks"
                          : "month"}{" "}
                      for 52 times (≈1 year)
                    </p>
                  </div>
                </>
              )}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Reason (optional)
              </label>
              <input
                type="text"
                value={blockForm.reason}
                onChange={(e) =>
                  setBlockForm((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="e.g., Vacation, Personal day"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowBlockModal(false)}
                style={{
                  padding: "8px 16px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBlockSubmit}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {editingException ? "Update Time Off" : "Submit Request"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Simplified Lunch Break Modal */}
      {showLunchModal && (
        <>
          <div
            onClick={() => setShowLunchModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "12px",
              padding: isMobile ? "20px" : "24px",
              zIndex: 1000,
              maxWidth: isMobile ? "90%" : "420px",
              width: "100%",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              🍽️ Set Lunch Break
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "20px",
              }}
            >
              Set when your lunch breaks start. The system will automatically
              block lunch time (30 minutes) on the selected days going forward.
            </p>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Start Date
              </label>
              <input
                type="date"
                value={lunchForm.startDate}
                onChange={(e) =>
                  setLunchForm((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                }}
              />
              <div
                style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}
              >
                When should lunch breaks begin?
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "4px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Start Time
                </label>
                <input
                  type="time"
                  value={lunchForm.startTime}
                  onChange={(e) =>
                    setLunchForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "4px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  End Time
                </label>
                <input
                  type="time"
                  value={lunchForm.endTime}
                  onChange={(e) =>
                    setLunchForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            {/* Days of Week Selection */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Which days should lunch breaks apply?
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                  gap: "8px",
                }}
              >
                {[
                  { value: 1, label: "Monday" },
                  { value: 2, label: "Tuesday" },
                  { value: 3, label: "Wednesday" },
                  { value: 4, label: "Thursday" },
                  { value: 5, label: "Friday" },
                  { value: 6, label: "Saturday" },
                  { value: 0, label: "Sunday" },
                ].map((day) => (
                  <label
                    key={day.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px",
                      border: (lunchForm.daysOfWeek || []).includes(day.value)
                        ? "2px solid #2563eb"
                        : "1px solid #d1d5db",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                      backgroundColor: (lunchForm.daysOfWeek || []).includes(
                        day.value,
                      )
                        ? "#eff6ff"
                        : "white",
                      transition: "all 0.2s",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={(lunchForm.daysOfWeek || []).includes(day.value)}
                      onChange={(e) => {
                        setLunchForm((prev) => ({
                          ...prev,
                          daysOfWeek: e.target.checked
                            ? [...(prev.daysOfWeek || []), day.value]
                            : (prev.daysOfWeek || []).filter(
                                (d) => d !== day.value,
                              ),
                        }));
                      }}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontWeight: "500" }}>{day.label}</span>
                  </label>
                ))}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#6b7280",
                  marginTop: "8px",
                }}
              >
                💡 Select all days you work (including weekends if applicable)
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowLunchModal(false)}
                style={{
                  padding: "8px 16px",
                  background: "white",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (
                    !lunchForm.startDate ||
                    !lunchForm.startTime ||
                    !lunchForm.endTime
                  ) {
                    alert("\u274c Please fill in all fields");
                    return;
                  }

                  if (
                    !lunchForm.daysOfWeek ||
                    lunchForm.daysOfWeek.length === 0
                  ) {
                    alert("\u274c Please select at least one day");
                    return;
                  }

                  try {
                    const token = localStorage.getItem("ogestyle_token");
                    const response = await fetch(
                      `${API_BASE_URL}/api/schedule-exceptions`,
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          startDate: lunchForm.startDate,
                          endDate: lunchForm.startDate,
                          isFullDayBlock: false,
                          timeBlocks: [
                            {
                              startTime: lunchForm.startTime,
                              endTime: lunchForm.endTime,
                            },
                          ],
                          type: "blocked",
                          reason: "Lunch Break",
                          recurring: {
                            enabled: true,
                            frequency: "weekly",
                            daysOfWeek: lunchForm.daysOfWeek || [1, 2, 3, 4, 5], // User-selected days or default weekdays
                            occurrences: 260, // ~5 years
                          },
                        }),
                      },
                    );

                    if (response.ok) {
                      alert("\u2705 Lunch breaks set successfully!");
                      setShowLunchModal(false);
                      fetchExceptions();
                      fetchBookings();
                    } else {
                      const error = await response.json();
                      if (
                        response.status === 409 &&
                        error.error === "booking_conflict"
                      ) {
                        setConflictBookings(error.affectedBookings || []);
                        setShowConflictModal(true);
                        setShowLunchModal(false);
                        return;
                      }
                      alert(error.message || "Failed to set lunch break");
                    }
                  } catch (error) {
                    alert("Failed to set lunch break");
                  }
                }}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Set Lunch Breaks
              </button>
            </div>
          </div>
        </>
      )}

      {/* Day Bookings Modal */}
      {showDayBookingsModal && (
        <>
          <div
            onClick={() => setShowDayBookingsModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
              zIndex: 1001,
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  All Bookings
                </h2>
                <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
                  {selectedDateStr}
                </p>
              </div>
              <button
                onClick={() => setShowDayBookingsModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "4px 8px",
                  lineHeight: "1",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {selectedDayBookings.map((booking) => {
                const getStatusColor = (status) => {
                  switch (status) {
                    case "confirmed":
                      return {
                        bg: "#dbeafe",
                        text: "#1e40af",
                        border: "#3b82f6",
                      };
                    case "completed":
                      return {
                        bg: "#d1fae5",
                        text: "#065f46",
                        border: "#10b981",
                      };
                    case "cancelled":
                      return {
                        bg: "#fee2e2",
                        text: "#991b1b",
                        border: "#ef4444",
                      };
                    case "pending":
                      return {
                        bg: "#fef3c7",
                        text: "#92400e",
                        border: "#f59e0b",
                      };
                    default:
                      return {
                        bg: "#f3f4f6",
                        text: "#374151",
                        border: "#9ca3af",
                      };
                  }
                };

                const statusColor = getStatusColor(booking.status);

                return (
                  <div
                    key={booking._id}
                    onClick={() => {
                      setShowDayBookingsModal(false);
                      navigate(
                        "/dashboard/professional/bookings/" + booking._id,
                      );
                    }}
                    style={{
                      padding: "16px",
                      border: `1px solid ${statusColor.border}`,
                      borderLeft: `4px solid ${statusColor.border}`,
                      borderRadius: "8px",
                      background: statusColor.bg,
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "8px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: statusColor.text,
                            marginBottom: "4px",
                          }}
                        >
                          {booking.serviceName}
                        </div>
                        <div
                          style={{ fontSize: "14px", color: statusColor.text }}
                        >
                          👤 {booking.customerName}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "4px 12px",
                          borderRadius: "6px",
                          background: "white",
                          color: statusColor.text,
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "capitalize",
                        }}
                      >
                        {booking.status}
                      </div>
                    </div>
                    <div style={{ fontSize: "14px", color: statusColor.text }}>
                      🕐{" "}
                      {formatTimeWithTimezone(
                        booking.startTime,
                        booking.appointmentDate,
                        shopInfo?.timezone || "America/Chicago",
                      )}{" "}
                      -{" "}
                      {formatTimeWithTimezone(
                        booking.endTime,
                        booking.appointmentDate,
                        shopInfo?.timezone || "America/Chicago",
                      )}
                      <span style={{ marginLeft: "8px", opacity: 0.7 }}>
                        ({booking.duration} min)
                      </span>
                    </div>
                    {booking.price && (
                      <div
                        style={{
                          fontSize: "14px",
                          color: statusColor.text,
                          marginTop: "4px",
                        }}
                      >
                        💰{" "}
                        {formatCurrency(
                          booking.price,
                          shopInfo?.currency || "USD",
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              style={{
                marginTop: "20px",
                paddingTop: "16px",
                borderTop: "1px solid #e5e7eb",
                textAlign: "center",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Total: {selectedDayBookings.length} booking
              {selectedDayBookings.length !== 1 ? "s" : ""}
            </div>
          </div>
        </>
      )}

      {/* Conflict Detection Modal */}
      {showConflictModal && (
        <>
          <div
            onClick={() => setShowConflictModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 999,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "12px",
              padding: isMobile ? "20px" : "24px",
              zIndex: 1000,
              maxWidth: isMobile ? "90%" : "600px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "32px" }}>⚠️</span>
              <h2
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "600",
                  margin: 0,
                }}
              >
                Cannot Block Dates - Existing Bookings
              </h2>
            </div>

            <p
              style={{
                color: "#dc2626",
                fontSize: "14px",
                marginBottom: "16px",
                lineHeight: "1.5",
              }}
            >
              You cannot block these dates because {conflictBookings.length}{" "}
              booking{conflictBookings.length !== 1 ? "s" : ""} already exist.
              These bookings must be cancelled or rescheduled before you can
              block time off.
            </p>

            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  color: "#dc2626",
                }}
              >
                Affected Bookings ({conflictBookings.length}):
              </h3>
              {conflictBookings.map((booking, idx) => (
                <div
                  key={booking._id || idx}
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: "6px",
                    marginBottom:
                      idx < conflictBookings.length - 1 ? "8px" : "0",
                    border: "1px solid #fecaca",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    📅 {booking.appointmentDate} at{" "}
                    {formatTime12Hour(booking.startTime)}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>
                    {booking.serviceName && `Service: ${booking.serviceName}`}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>
                    👤 Customer: {booking.customerName || booking.customerEmail}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #93c5fd",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  color: "#1e40af",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                <strong>Next Steps:</strong>
                <br />
                Contact your business owner/manager to cancel or reschedule
                these bookings. Once the bookings are removed, you can try
                blocking these dates again.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictBookings([]);
                }}
                style={{
                  padding: "8px 20px",
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Understood
              </button>
            </div>
          </div>
        </>
      )}
    </StaffSidebarLayout>
  );
};

export default UnifiedScheduleCalendar;
